/**
 * Procedural rig for AI base models. A base model ships only a handful of landmarks (joint positions in the normalised
 * model space: feet on y = 0, height 1, centred, facing +z, character's right = −x). From them we build a bone tree and
 * compute 4-bone linear-blend skin weights:
 *  1. every vertex is classified to its "owner" bone by capsule distance relative to the bone radius (so a vertex on a
 *     thin arm hugging the torso is still owned by the arm);
 *  2. weights are spread to the owner, its parent and its children with a smooth falloff along the bone (joints blend),
 *  3. weights are smoothed over the mesh adjacency (welded) a few times, restricted to the owner's chain.
 */

export type V3 = [number, number, number];

export interface Landmarks {
  pelvis: V3; chest: V3; neck: V3; head: V3; headTop: V3;
  shoulderR: V3; elbowR: V3; wristR: V3; handR: V3;
  shoulderL: V3; elbowL: V3; wristL: V3; handL: V3;
  hipR: V3; kneeR: V3; ankleR: V3; toeR: V3;
  hipL: V3; kneeL: V3; ankleL: V3; toeL: V3;
  /** Tail polyline from the root to the tip (≥ 2 points). */
  tail: V3[];
  earR?: V3; earL?: V3;
  /** Optional anatomical ear paths, root → bends → tip, for long or folded ears. */
  earRPath?: V3[]; earLPath?: V3[];
  /** Face frame: point between the eyes on the surface + eye spacing (procedural face). */
  face?: { center: V3; scale: number };
  /** Radii (in model heights) used for ownership and garments. */
  radius: { torso: number; head: number; arm: number; leg: number; tail: number };
}

export interface Bone {
  name: string;
  parent: number;
  /** Joint (pivot) in rest model space. */
  head: V3;
  /** End point (child joint or tip) in rest model space. */
  tail: V3;
  radius: number;
  chain: 'spine' | 'armR' | 'armL' | 'legR' | 'legL' | 'tail' | 'head' | 'ear' | 'prop';
}

export interface Skeleton { bones: Bone[]; byName: Map<string, number> }

export function buildSkeleton(l: Landmarks): Skeleton {
  const bones: Bone[] = [];
  const add = (name: string, parent: string | null, head: V3, tail: V3, radius: number, chain: Bone['chain']): void => {
    bones.push({ name, parent: parent === null ? -1 : bones.findIndex((b) => b.name === parent), head, tail, radius, chain });
  };
  const r = l.radius;
  add('pelvis', null, l.pelvis, l.chest, r.torso, 'spine');
  add('chest', 'pelvis', l.chest, l.neck, r.torso * 0.95, 'spine');
  add('neck', 'chest', l.neck, l.head, r.torso * 0.55, 'spine');
  add('head', 'neck', l.head, l.headTop, r.head, 'head');
  for (const side of ['R', 'L'] as const) {
    const tip = l[`ear${side}`], path = l[`ear${side}Path`] ?? (tip ? [[tip[0] * 0.6 + l.head[0] * 0.4, tip[1] - 0.06, tip[2]] as V3, tip] : []);
    for (let i = 0; i + 1 < path.length; i++) {
      const name = `ear${side}${i || ''}`, parent = i === 0 ? 'head' : `ear${side}${i > 1 ? i - 1 : ''}`;
      add(name, parent, path[i], path[i + 1], r.head * 0.3, 'ear');
    }
  }
  for (const s of ['R', 'L'] as const) {
    const g = (k: string) => (l as unknown as Record<string, V3>)[k + s];
    add(`upperArm${s}`, 'chest', g('shoulder'), g('elbow'), r.arm, `arm${s}`);
    add(`foreArm${s}`, `upperArm${s}`, g('elbow'), g('wrist'), r.arm * 0.9, `arm${s}`);
    add(`hand${s}`, `foreArm${s}`, g('wrist'), g('hand'), r.arm * 0.9, `arm${s}`);
    add(`thigh${s}`, 'pelvis', g('hip'), g('knee'), r.leg, `leg${s}`);
    add(`shin${s}`, `thigh${s}`, g('knee'), g('ankle'), r.leg * 0.85, `leg${s}`);
    add(`foot${s}`, `shin${s}`, g('ankle'), g('toe'), r.leg * 0.85, `leg${s}`);
  }
  for (let i = 0; i < l.tail.length - 1; i++) {
    add(`tail${i}`, i === 0 ? 'pelvis' : `tail${i - 1}`, l.tail[i], l.tail[i + 1], r.tail, 'tail');
  }
  // Append-only rigid attachments. They never own skin vertices; zero offset
  // exactly reproduces the hand's old weapon matrix and all existing indices.
  for(const side of ['R','L'] as const){const hand=bones.find(b=>b.name==='hand'+side)!;add('weapon'+side,'hand'+side,hand.head,hand.tail,hand.radius,'prop');}
  const byName = new Map(bones.map((b, i) => [b.name, i]));
  return { bones, byName };
}

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** Distance from p to the segment a→b and the segment parameter t in 0..1. */
export function segDist(p: V3, a: V3, b: V3): { d: number; t: number } {
  const ab = sub(b, a), ap = sub(p, a);
  const t = Math.max(0, Math.min(1, dot(ap, ab) / Math.max(1e-9, dot(ab, ab))));
  const q: V3 = [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t];
  return { d: Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]), t };
}

export interface SkinData {
  joints: Uint8Array;   // 4 per vertex
  weights: Float32Array; // 4 per vertex, sum 1
  owner: Uint8Array;     // dominant bone per vertex (debug colouring, garment regions)
}

/** positions: normalised model space (3 per vertex); indices: triangles. */
export function computeSkin(positions: Float32Array, indices: Uint32Array, sk: Skeleton): SkinData {
  const vc = positions.length / 3;
  const B = sk.bones.length;
  const owner = new Uint8Array(vc);
  const raw = new Float32Array(vc * B);
  const p: V3 = [0, 0, 0];
  const assign = (v: number, best: number, t: number): void => {
    owner[v] = best;
    raw.fill(0, v * B, (v + 1) * B);
    // spread: owner + parent + children with joint blending near the ends
    const o = sk.bones[best];
    raw[v * B + best] = 1;
    const blend = 0.22; // fraction of the bone length that blends into the neighbour across a joint
    if (o.parent >= 0 && t < blend && o.chain !== 'ear') {
      const k = 0.5 * (1 - t / blend);
      raw[v * B + o.parent] += k / (1 - k + 1e-6) * 0.6;
    }
    if (t > 1 - blend) {
      for (let c = 0; c < B; c++) if (sk.bones[c].parent === best && sk.bones[c].chain === o.chain) {
        const k = 0.5 * ((t - (1 - blend)) / blend);
        raw[v * B + c] += k / (1 - k + 1e-6) * 0.6;
      }
    }
  };
  for (let v = 0; v < vc; v++) {
    p[0] = positions[v * 3]; p[1] = positions[v * 3 + 1]; p[2] = positions[v * 3 + 2];
    let best = 0, bestScore = Infinity, bestT = 0;
    for (let b = 0; b < B; b++) {
      if(sk.bones[b].chain==='prop')continue;
      const bone = sk.bones[b], { d, t } = segDist(p, bone.head, bone.tail), score = d / bone.radius;
      if (score < bestScore) { bestScore = score; best = b; bestT = t; }
    }
    assign(v, best, bestT);
  }
  // smoothing over welded adjacency, within the owner's chain neighbourhood
  const key = (i: number) => `${Math.round(positions[i * 3] * 2e4)},${Math.round(positions[i * 3 + 1] * 2e4)},${Math.round(positions[i * 3 + 2] * 2e4)}`;
  const weld = new Map<string, number>(); const group = new Int32Array(vc);
  for (let i = 0; i < vc; i++) { const k = key(i); let g = weld.get(k); if (g === undefined) { g = weld.size; weld.set(k, g); } group[i] = g; }
  const G = weld.size;
  const nbr: Set<number>[] = Array.from({ length: G }, () => new Set<number>());
  for (let t = 0; t < indices.length; t += 3) {
    const a = group[indices[t]], b = group[indices[t + 1]], c = group[indices[t + 2]];
    nbr[a].add(b); nbr[a].add(c); nbr[b].add(a); nbr[b].add(c); nbr[c].add(a); nbr[c].add(b);
  }
  // Meshy tails are often separate mesh islands. A curled tip can sit next to
  // the chest or head, but should never inherit clothing or their deformation.
  // If the island is predominantly a tail, assign the whole island to that chain.
  const representative = new Int32Array(G), visited = new Uint8Array(G), tailIsland = new Uint8Array(G);
  for (let v = 0; v < vc; v++) representative[group[v]] = v;
  for (let start = 0; start < G; start++) {
    if (visited[start]) continue;
    const component: number[] = [start]; visited[start] = 1;
    let tail = 0;
    for (let cursor = 0; cursor < component.length; cursor++) {
      const g = component[cursor];
      if (sk.bones[owner[representative[g]]].chain === 'tail') tail++;
      for (const n of nbr[g]) if (!visited[n]) { visited[n] = 1; component.push(n); }
    }
    if (component.length >= 8 && tail / component.length > 0.55) for (const g of component) tailIsland[g] = 1;
  }
  // On a connected curled tail, one concave vertex may sit closer to the
  // pelvis capsule than to the tail centreline. Its complete topological ring
  // still belongs to the tail. Repair that isolated label without growing the
  // tail through a legitimate mixed torso/root boundary.
  for(let g=0;g<G;g++){
    const neighbors=[...nbr[g]].filter(n=>n!==g);
    if(neighbors.length>=3&&neighbors.every(n=>sk.bones[owner[representative[n]]].chain==='tail'))tailIsland[g]=1;
  }
  const tailBones = sk.bones.map((bone, i) => bone.chain === 'tail' ? i : -1).filter(i => i >= 0);
  for (let v = 0; v < vc; v++) if (tailIsland[group[v]]) {
    p[0] = positions[v * 3]; p[1] = positions[v * 3 + 1]; p[2] = positions[v * 3 + 2];
    let best = tailBones[0], bestScore = Infinity, bestT = 0;
    for (const i of tailBones) {
      const bone = sk.bones[i], { d, t } = segDist(p, bone.head, bone.tail), score = d / bone.radius;
      if (score < bestScore) { best = i; bestScore = score; bestT = t; }
    }
    assign(v, best, bestT);
  }
  let gw = new Float32Array(G * B);
  const gcount = new Float32Array(G);
  for (let v = 0; v < vc; v++) { gcount[group[v]]++; for (let b = 0; b < B; b++) gw[group[v] * B + b] += raw[v * B + b]; }
  for (let g = 0; g < G; g++) { let s = 0; for (let b = 0; b < B; b++) s += gw[g * B + b]; if (s > 0) for (let b = 0; b < B; b++) gw[g * B + b] /= s; }
  const gOwner = new Uint8Array(G);
  for (let v = 0; v < vc; v++) gOwner[group[v]] = owner[v];
  const related = (a: number, b: number): boolean => {
    if (a === b) return true;
    const A = sk.bones[a], Bn = sk.bones[b];
    return A.parent === b || Bn.parent === a || (A.chain === Bn.chain && A.chain !== 'spine') || (A.parent >= 0 && A.parent === Bn.parent && A.chain === Bn.chain);
  };
  for (let it = 0; it < 3; it++) {
    const next = new Float32Array(gw);
    for (let g = 0; g < G; g++) {
      const ns = [...nbr[g]].filter((n) => related(gOwner[n], gOwner[g]));
      if (!ns.length) continue;
      for (let b = 0; b < B; b++) {
        let s = gw[g * B + b] * 2;
        for (const n of ns) s += gw[n * B + b];
        next[g * B + b] = s / (2 + ns.length);
      }
    }
    gw = next;
  }
  // top-4 per vertex
  const joints = new Uint8Array(vc * 4), weights = new Float32Array(vc * 4);
  for (let v = 0; v < vc; v++) {
    const g = group[v];
    const pairs: [number, number][] = [];
    for (let b = 0; b < B; b++) { const w = gw[g * B + b]; if (w > 0.001) pairs.push([b, w]); }
    pairs.sort((x, y) => y[1] - x[1]);
    const top = pairs.slice(0, 4);
    const s = top.reduce((a, x) => a + x[1], 0) || 1;
    for (let k = 0; k < 4; k++) { joints[v * 4 + k] = top[k]?.[0] ?? 0; weights[v * 4 + k] = top[k] ? top[k][1] / s : 0; }
    if (!top.length) { joints[v * 4] = owner[v]; weights[v * 4] = 1; }
  }
  return { joints, weights, owner };
}
